import styled from '@emotion/styled';
import {
  palette,
  spacing,
  radius,
  shadow,
  transition,
  duration,
  easing,
  layout,
  zIndex,
} from '../../../style-utils/theme';
import { up, down } from '../../../style-utils/media-queries';
import { ActiveIndicator } from '../../../motion';

/**
 * The employee / wizard shell rail.
 *
 * lg+   : full desk shell — 80px icon rail + building-context panel.
 * md–lg : icon rail only, except the main menu keeps its required building
 *         context beside the rail.
 * < md  : the rail detaches into a frosted bottom tab bar.
 */

/* Frosted-glass material for the mobile bar (overlaps content). No opaque
   palette token can express translucency, so the frost is kept local. */
export const SidebarWrapper = styled.div<{ showBuildingContext?: boolean }>`
  display: flex;
  flex-shrink: 0;
  min-width: 0;

  ${up('lg')} {
    width: 320px;
  }
  ${down('lg')} {
    width: ${({ showBuildingContext }) =>
      showBuildingContext ? '320px' : layout.sidebarCollapsed};
  }
  ${down('md')} {
    width: 100%;
  }
`;

export const BuildingDataWrapper = styled.div<{ showOnCompact?: boolean }>`
  display: flex;
  flex: 1;
  min-width: 0;
  padding: ${spacing.md};
  background: ${palette.surface};
  border-right: 1px solid ${palette.line};

  ${down('lg')} {
    display: ${({ showOnCompact }) => (showOnCompact ? 'flex' : 'none')};
  }

  ${down('md')} {
    width: 100%;
    padding: ${spacing.sm};
    border-right: none;
    border-bottom: 1px solid ${palette.line};
  }
`;

export const SidebarContainer = styled.div`
  width: ${layout.sidebarCollapsed};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm} 0;
  background: ${palette.surface};
  border-right: 1px solid ${palette.line};

  ${down('md')} {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    min-height: ${layout.navHeight};
    flex-direction: row;
    gap: ${spacing.sm};
    padding: 0 ${spacing.md} var(--safe-bottom);
    border-right: none;
    border-top: 1px solid ${palette.line};
    background: ${palette.surfaceGlass};
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: ${shadow.lg};
    z-index: ${zIndex.sticky};
  }
`;

export const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  position: relative;

  ${down('md')} {
    flex-direction: row;
    gap: ${spacing.sm};
  }
`;

export const IconWrapper = styled.button<{ isHighlighted: boolean }>`
  position: relative;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: none;
  border-radius: ${radius.sm};
  color: ${({ isHighlighted }) =>
    isHighlighted ? palette.iris : palette.slate};
  background: ${({ isHighlighted }) =>
    isHighlighted ? palette.irisTint : 'transparent'};
  box-shadow: ${({ isHighlighted }) => (isHighlighted ? shadow.sm : 'none')};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover {
    background: ${({ isHighlighted }) =>
      isHighlighted ? palette.irisTint : palette.irisTintSoft};
    transform: translateY(-1px);
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:active {
    transform: scale(0.97);
  }
`;

/* THE SLIDING IRIS ACCENT — one element, rendered inside the highlighted
   IconWrapper only; framer's layoutId moves it to the other destination with
   the indicator spring. Offsets are arithmetic, not a translate, because the
   layout animation owns this element's transform. Under md the rail is the
   bottom tab bar and the accent becomes the 24x3 underline. */
export const DeskRailIndicator = styled(ActiveIndicator)`
  position: absolute;
  left: -2px;
  top: calc(50% - 12px);
  width: 3px;
  height: 24px;
  border-radius: ${radius.pill};
  background: ${palette.iris};
  pointer-events: none;

  ${down('md')} {
    left: calc(50% - 12px);
    top: auto;
    bottom: -2px;
    width: 24px;
    height: 3px;
  }
`;
